import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { coachService } from '../../../services/performanceService';

const CoachPerformanceForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    // Coach Profile
    fullName: '',
    nationality: '',
    coachingLicense: '',
    preferredFormation: '',
    coachingStyle: '',
    // Career History
    previousClubs: '',
    presentClub: '',
    competitionsManaged: '',
    // Performance Statistics
    totalGamesManaged: '',
    gamesWon: '',
    gamesDrawn: '',
    gamesLost: '',
    // Achievements
    cupsWon: '',
    leagueTitles: '',
    individualAwards: '',
    // National Team Experience
    nationalTeamManaged: false,
    nationalTeamLevel: '',
    // Media
    coachPhoto: null,
    certificates: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const licenseOptions = [
    { value: 'uefa-pro', label: 'UEFA Pro License' },
    { value: 'uefa-a', label: 'UEFA A License' },
    { value: 'uefa-b', label: 'UEFA B License' },
    { value: 'caf-a', label: 'CAF A License' },
    { value: 'caf-b', label: 'CAF B License' },
    { value: 'other', label: 'Other License' }
  ];

  const formationOptions = [
    { value: '4-3-3', label: '4-3-3' },
    { value: '4-4-2', label: '4-4-2' },
    { value: '3-5-2', label: '3-5-2' },
    { value: '4-2-3-1', label: '4-2-3-1' },
    { value: '3-4-3', label: '3-4-3' },
    { value: '5-3-2', label: '5-3-2' }
  ];

  const nationalTeamLevelOptions = [
    { value: 'youth', label: 'Youth Level' },
    { value: 'senior', label: 'Senior Level' },
    { value: 'both', label: 'Both Youth & Senior' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files?.[0] : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.nationality?.trim()) newErrors.nationality = 'Nationality is required';
    if (!formData?.coachingLicense) newErrors.coachingLicense = 'Coaching license is required';
    if (!formData?.presentClub?.trim()) newErrors.presentClub = 'Present club is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare files object
      const files = {
        coachPhoto: formData?.coachPhoto,
        certificates: formData?.certificates
      };
      
      // Prepare data object (exclude file objects)
      const coachData = {
        fullName: formData?.fullName,
        nationality: formData?.nationality,
        coachingLicense: formData?.coachingLicense,
        preferredFormation: formData?.preferredFormation,
        coachingStyle: formData?.coachingStyle,
        previousClubs: formData?.previousClubs,
        presentClub: formData?.presentClub,
        competitionsManaged: formData?.competitionsManaged,
        totalGamesManaged: parseInt(formData?.totalGamesManaged) || 0,
        gamesWon: parseInt(formData?.gamesWon) || 0,
        gamesDrawn: parseInt(formData?.gamesDrawn) || 0,
        gamesLost: parseInt(formData?.gamesLost) || 0,
        cupsWon: formData?.cupsWon,
        leagueTitles: formData?.leagueTitles,
        individualAwards: formData?.individualAwards,
        nationalTeamManaged: formData?.nationalTeamManaged,
        nationalTeamLevel: formData?.nationalTeamLevel
      };
      
      const { data, error } = await coachService?.create(coachData, files);
      
      if (error) {
        console.error('Error creating coach record:', error);
        setErrors({ submit: error?.message || 'Failed to submit coach record' });
        return;
      }
      
      onSubmit?.();
      
      // Reset form
      setFormData({
        fullName: '', nationality: '', coachingLicense: '', preferredFormation: '',
        coachingStyle: '', previousClubs: '', presentClub: '', competitionsManaged: '',
        totalGamesManaged: '', gamesWon: '', gamesDrawn: '', gamesLost: '',
        cupsWon: '', leagueTitles: '', individualAwards: '',
        nationalTeamManaged: false, nationalTeamLevel: '',
        coachPhoto: null, certificates: null
      });
    } catch (error) {
      console.error('Submit error:', error);
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateWinPercentage = () => {
    const total = parseInt(formData?.totalGamesManaged) || 0;
    const won = parseInt(formData?.gamesWon) || 0;
    if (total === 0) return '0.00';
    return ((won / total) * 100)?.toFixed(2);
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
      {/* Coach Profile Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Clipboard" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Coach Profile Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            name="fullName"
            value={formData?.fullName}
            onChange={handleChange}
            placeholder="Enter full name"
            required
            error={errors?.fullName}
          />
          <Input
            label="Nationality"
            name="nationality"
            value={formData?.nationality}
            onChange={handleChange}
            placeholder="Enter nationality"
            required
            error={errors?.nationality}
          />
          <Select
            label="Coaching License Level"
            name="coachingLicense"
            options={licenseOptions}
            value={formData?.coachingLicense}
            onChange={(value) => handleSelectChange('coachingLicense', value)}
            placeholder="Select license level"
            required
            error={errors?.coachingLicense}
          />
          <Select
            label="Preferred Formation"
            name="preferredFormation"
            options={formationOptions}
            value={formData?.preferredFormation}
            onChange={(value) => handleSelectChange('preferredFormation', value)}
            placeholder="Select formation"
          />
          <div className="md:col-span-2">
            <Input
              label="Coaching Style"
              name="coachingStyle"
              value={formData?.coachingStyle}
              onChange={handleChange}
              placeholder="e.g., Possession-based, Counter-attacking, High-pressing"
              description="Describe your tactical philosophy"
            />
          </div>
        </div>
      </div>
      {/* Career History */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Building2" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Career History</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Previous Clubs"
            name="previousClubs"
            value={formData?.previousClubs}
            onChange={handleChange}
            placeholder="e.g., Club A (2020-2023), Club B (2018-2020)"
            description="List previous clubs with seasons"
          />
          <Input
            label="Present Club"
            name="presentClub"
            value={formData?.presentClub}
            onChange={handleChange}
            placeholder="Enter current club"
            required
            error={errors?.presentClub}
          />
          <div className="md:col-span-2">
            <Input
              label="Competitions Managed"
              name="competitionsManaged"
              value={formData?.competitionsManaged}
              onChange={handleChange}
              placeholder="e.g., Premier League, FA Cup, Champions League"
            />
          </div>
        </div>
      </div>
      {/* Performance Statistics */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="BarChart3" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Performance Statistics</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            label="Total Games Managed"
            name="totalGamesManaged"
            type="number"
            value={formData?.totalGamesManaged}
            onChange={handleChange}
            placeholder="Total games"
          />
          <Input
            label="Games Won"
            name="gamesWon"
            type="number"
            value={formData?.gamesWon}
            onChange={handleChange}
            placeholder="Wins"
          />
          <Input
            label="Games Drawn"
            name="gamesDrawn"
            type="number"
            value={formData?.gamesDrawn}
            onChange={handleChange}
            placeholder="Draws"
          />
          <Input
            label="Games Lost"
            name="gamesLost"
            type="number"
            value={formData?.gamesLost}
            onChange={handleChange}
            placeholder="Losses"
          />
        </div>
        
        {formData?.totalGamesManaged && (
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Win Percentage</span>
              <span className="text-2xl font-bold text-primary">{calculateWinPercentage()}%</span>
            </div>
          </div>
        )}
      </div>
      {/* Achievements */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Trophy" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Achievements</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Cups Won"
            name="cupsWon"
            value={formData?.cupsWon}
            onChange={handleChange}
            placeholder="e.g., FA Cup (2022), League Cup (2023)"
          />
          <Input
            label="League Titles"
            name="leagueTitles"
            value={formData?.leagueTitles}
            onChange={handleChange}
            placeholder="e.g., Premier League (2021)"
          />
          <div className="md:col-span-2">
            <Input
              label="Individual Awards"
              name="individualAwards"
              value={formData?.individualAwards}
              onChange={handleChange}
              placeholder="e.g., Manager of the Year (2022), Best Coach Award"
            />
          </div>
        </div>
      </div>
      {/* National Team Experience */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Flag" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">National Team Experience</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <Checkbox
              id="nationalTeamManaged"
              checked={formData?.nationalTeamManaged}
              onCheckedChange={(checked) => handleSelectChange('nationalTeamManaged', checked)}
            />
            <label htmlFor="nationalTeamManaged" className="text-sm font-medium cursor-pointer">
              National Team Management Experience
            </label>
          </div>
          
          {formData?.nationalTeamManaged && (
            <Select
              label="National Team Level"
              name="nationalTeamLevel"
              options={nationalTeamLevelOptions}
              value={formData?.nationalTeamLevel}
              onChange={(value) => handleSelectChange('nationalTeamLevel', value)}
              placeholder="Select level"
            />
          )}
        </div>
      </div>
      {/* Media */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Upload" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Media</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Coach Photo"
            name="coachPhoto"
            type="file"
            onChange={handleChange}
            description="Upload professional photo"
          />
          <Input
            label="Certificates"
            name="certificates"
            type="file"
            onChange={handleChange}
            description="Upload coaching certificates (optional)"
          />
        </div>
      </div>
      {/* Submit Button */}
      <div className="flex justify-end gap-4 pt-6 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location?.reload()}
        >
          Reset Form
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          iconName="Save"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Coach Record'}
        </Button>
      </div>
    </form>
  );
};

export default CoachPerformanceForm;